import {
  INTAKE_SEARCH_WAIT_MS,
  INTAKE_WAIT_MS,
  IntakeRequestError,
  POLL_MS,
  type intakeApi,
  type IntakeJob,
} from '../services/intakeApi';

/** Why intake ended without a draft; each maps to one line of copy. */
export type IntakeFailure =
  | 'quota'
  | 'badUrl'
  | 'badFile'
  | 'fetch'
  | 'tooLittle'
  | 'timeout'
  | 'failed';

export type IntakeOutcome =
  | { kind: 'done'; job: IntakeJob }
  | {
      kind: 'failed';
      reason: IntakeFailure;
      /** A draft the job wrote before the abandon landed: the submission must reuse it. */
      draftPickId?: string | null;
      /** The abandon could not be confirmed: abandon again before submitting. */
      pendingJobId?: string;
    }
  | { kind: 'cancelled' };

type Api = ReturnType<typeof intakeApi>;

export interface IntakeDeps {
  api: Api;
  /** Shrinks an image to what the model accepts (under 4 MB, long edge 2560). */
  compress: (file: File) => Promise<Blob>;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  /** The page would not open and the server is searching the web for it instead. */
  onSearching?: () => void;
}

const MAX_UPLOAD = 10 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function failureFor(err: unknown): IntakeFailure {
  if (err instanceof IntakeRequestError) {
    if (err.status === 429) return 'quota';
    if (err.status === 413 || err.code === 'UNSUPPORTED_TYPE') return 'badFile';
    if (err.code === 'BAD_URL') return 'badUrl';
  }
  return 'failed';
}

function failureForJob(error: string | null): IntakeFailure {
  if (error === 'FETCH_FAILED') return 'fetch';
  if (error === 'TOO_LITTLE_TEXT') return 'tooLittle';
  if (error === 'IMAGE_TOO_LARGE') return 'badFile';
  if (error === 'EXPIRED') return 'timeout';
  return 'failed';
}

/**
 * One intake attempt (PRD §6.2): upload if needed, start the job, poll for up to
 * 60 seconds, or two minutes from when a web search fallback begins. On timeout the
 * job is abandoned so a late result cannot land after the person has started typing;
 * if the draft won the race it is used.
 */
export async function runIntake(
  input: { url: string } | { file: File },
  signal: AbortSignal,
  deps: IntakeDeps
): Promise<IntakeOutcome> {
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = deps.now ?? (() => Date.now());
  const { api } = deps;

  let jobId: string;
  try {
    if ('file' in input) {
      if (!IMAGE_TYPES.includes(input.file.type)) return { kind: 'failed', reason: 'badFile' };
      let blob: Blob;
      try {
        blob = await deps.compress(input.file);
      } catch {
        return { kind: 'failed', reason: 'badFile' };
      }
      if (blob.size > MAX_UPLOAD) return { kind: 'failed', reason: 'badFile' };
      if (signal.aborted) return { kind: 'cancelled' };
      const { uploadId } = await api.upload(blob);
      if (signal.aborted) return { kind: 'cancelled' };
      ({ jobId } = await api.start({ kind: 'IMAGE', uploadId }));
    } else {
      ({ jobId } = await api.start({ kind: 'URL', url: input.url.trim() }));
    }
  } catch (err) {
    return { kind: 'failed', reason: failureFor(err) };
  }

  let deadline = now() + INTAKE_WAIT_MS;
  let searching = false;
  for (;;) {
    if (signal.aborted) {
      void api.abandon(jobId).catch(() => undefined);
      return { kind: 'cancelled' };
    }
    if (now() >= deadline) break;
    await sleep(POLL_MS);
    if (signal.aborted) continue;
    let job: IntakeJob;
    try {
      job = await api.get(jobId);
    } catch {
      continue; // A dropped poll is retried until the deadline.
    }
    if (job.status === 'DONE') return { kind: 'done', job };
    if (job.status === 'RUNNING' && job.viaSearch && !searching) {
      searching = true;
      deadline = Math.max(deadline, now() + INTAKE_SEARCH_WAIT_MS);
      deps.onSearching?.();
    }
    if (job.status === 'FAILED') return { kind: 'failed', reason: failureForJob(job.error) };
    if (job.status === 'ABANDONED') return { kind: 'failed', reason: 'timeout' };
  }

  // The abandon must land (or reveal the draft) before manual entry can be submitted.
  let after: { status: IntakeJob['status']; draftPickId: string | null } | null = null;
  for (let attempt = 0; attempt < 3 && !after; attempt++) {
    try {
      after = await api.abandon(jobId);
    } catch {
      await sleep(1000);
    }
  }
  if (!after) return { kind: 'failed', reason: 'timeout', pendingJobId: jobId };
  if (after.status === 'DONE') {
    try {
      const job = await api.get(jobId);
      if (job.status === 'DONE') return { kind: 'done', job };
    } catch {
      // Falls through: the draft id alone is enough to reuse it.
    }
    return { kind: 'failed', reason: 'timeout', draftPickId: after.draftPickId };
  }
  return { kind: 'failed', reason: 'timeout' };
}

/**
 * Before a manual submission: makes sure a job whose abandon was never confirmed can
 * no longer write a draft, and returns the draft it wrote if it already did. Throws
 * when the server cannot be reached, so the caller does not submit a second draft.
 */
export async function settlePendingJob(api: Api, jobId: string): Promise<string | null> {
  const after = await api.abandon(jobId);
  return after.status === 'DONE' ? after.draftPickId : null;
}
